import { useRef, useState } from 'react'

import {
	Chip,
	Fade,
	Paper,
	Popper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import { getCurrencyName, largeNumToString } from '@utils/stringHelpers'

type KPIProps = {
	icon: React.ReactNode
	label: string
	value: number
	chip?: React.ReactNode
	currency?: string
	details?: { label: string; value: number; percentage: number }[]
}

export default function KPI({
	icon,
	label,
	value,
	chip = null,
	currency = 'USD',
	details = [],
}: KPIProps) {
	const theme = useTheme()
	const largeScreen = useMediaQuery(theme.breakpoints.up('xl'))
	const [open, setOpen] = useState(false)
	const anchorRef = useRef<HTMLDivElement | null>(null)

	return (
		<>
			<Stack
				ref={anchorRef}
				onMouseEnter={() => setOpen(true)}
				onMouseLeave={() => setOpen(false)}
				mr={2}
				px={1}
				gap={1}
				direction="row"
				alignItems="center"
				justifyContent="flex-start"
			>
				{icon}
				{largeScreen && (
					<Typography noWrap variant="body1">
						{label}
					</Typography>
				)}
				<Typography noWrap variant="h6">
					{value != null ? largeNumToString(value) : '--'}
				</Typography>
				{chip}
			</Stack>

			<Popper
				open={open && details.length > 0}
				anchorEl={anchorRef.current}
				placement="bottom-start"
				transition
				style={{ zIndex: 1300 }}
				modifiers={[
					{
						name: 'offset',
						options: {
							offset: [0, 8],
						},
					},
				]}
			>
				{({ TransitionProps }) => (
					<Fade {...TransitionProps} timeout={200}>
						<Paper sx={{ p: 1, minWidth: 150, zIndex: 10 }}>
							<Stack spacing={1} sx={{ p: 2 }}>
								{details.map((item) => (
									<KpiDetail
										key={item.label}
										label={item.label}
										value={item.value}
										percentage={item.percentage}
										currency={currency}
									/>
								))}
							</Stack>
						</Paper>
					</Fade>
				)}
			</Popper>
		</>
	)
}

function KpiDetail({
	label,
	value,
	percentage,
	currency = 'USD',
}: {
	label: string
	value: number
	percentage: number
	currency?: string
}) {
	const { symbol } = getCurrencyName(currency)

	return (
		<Stack direction="row" alignItems="flex-start" gap={2}>
			<Typography variant="body2">
				<b>{`${symbol}${value != null ? largeNumToString(value) : '--'}`}</b>
			</Typography>
			<Typography variant="body2"> {label} </Typography>
			<Chip
				size="small"
				color={value > 0 ? 'success' : 'error'}
				label={`${percentage !== null ? percentage : '--'}%`}
			/>
		</Stack>
	)
}
